/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import classNames from 'classnames';
import {
  Divider,
  Tab,
  TabBox,
  TabPanel,
  Tabs,
  ToolTip,
  useAlert,
  useToast,
} from 'fasoo-ui-component-next';
import _ from 'lodash';

import approvalDetailApi, {
  ApprovalDetailEntity,
  ApprovalStatusType,
  RequestApprovalDto,
} from 'src/api/user/approvalApi';
import scanLedgerApprovalApi, {
  ScanLedgerApprovalDetailEntity,
  ScanLedgerUsageFilesDto,
  ScanLedgerUsageFilesItem,
  ScanLedgerUsagesType,
} from 'src/api/user/scanLedgerApprovalApi';
import { CommonProps } from 'src/components/helpers/props';
import useIntersectionObserver from 'src/hooks/useIntersectionObserverTarget';
import usePopup from 'src/hooks/usePopup';
import useProgressStore from 'src/hooks/useProgressStore';
import { getFasooUiComponentLanguage } from 'src/i18n';
import { toLocalDateTimeLangStr } from 'src/shared/utils/localDateTime';

import { ApprovalDetailFooter } from '../ApprovalDetailFooter';

import { ApprovalTypeIconArea } from './ApprovalTypeIconArea';
import RemarkPopup from './RemarkPopup';
import { ApprovalUserInfoDto, FileIcon, Link, ShowLineContent, UserDetail } from '.';

import './ScanLedgerDetail.scss';

interface ScanLedgerDetailProps extends CommonProps {
  scanLedgerApproval: ScanLedgerApprovalDetailEntity;
  ResetDetail: () => void;
  cancelApprovalChildren?: React.ReactNode;
  onCloseDetail: (open: boolean, isReject?: boolean) => void;
  isComplete?: boolean;
  selectedApproval?: ApprovalDetailEntity | null;
}

export const ScanLedgerDetail: React.FC<ScanLedgerDetailProps> = ({
  scanLedgerApproval,
  ResetDetail,
  cancelApprovalChildren,
  onCloseDetail,
  isComplete = false,
  selectedApproval,
}) => {
  const { t } = useTranslation(['menu', 'userApproval']);
  const toast = useToast();
  const { alert } = useAlert();
  const openApprovalPopup = usePopup();
  const openRejectPopup = usePopup();
  const [tab, setTab] = useState<number>(0);
  const [fileCount, setFileCount] = useState<number>(0);
  const [usageFiles, setUsageFiles] = useState<ScanLedgerUsageFilesItem[]>([]);
  const { startProgress, stopProgress } = useProgressStore();
  let approvers: ApprovalUserInfoDto[] = [];

  if (scanLedgerApproval.completeApprovers.length > 0) {
    const completes = _.sortBy(scanLedgerApproval.completeApprovers, 'step');
    approvers = approvers.concat(
      completes.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: approver.approvalStatus,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
          actorIp: approver.actorIp,
          approvalRemark: approver.approvalRemark,
          approvalDate: approver.updatedTs,
          method: approver.approvalMethod,
          otherDeptCode: approver.otherDeptCode === null ? null : approver.otherDeptCode,
          otherDeptName: approver.otherDeptName === null ? null : approver.otherDeptName,
          otherPositionCode:
            approver.otherPositionCode === null ? null : approver.otherPositionCode,
          otherPositionName:
            approver.otherPositionName === null ? null : approver.otherPositionName,
          otherRoleCode: approver.otherRoleCode === null ? null : approver.otherRoleCode,
          otherRoleName: approver.otherRoleName === null ? null : approver.otherRoleName,
          otherUserId: approver.otherUserId === null ? null : approver.otherUserId,
          otherUserName: approver.otherUserName === null ? null : approver.otherUserName,
        };
      })
    );
  }
  if (scanLedgerApproval.progressApprovers.length > 0) {
    const progresses = _.sortBy(scanLedgerApproval.progressApprovers, 'step');
    approvers = approvers.concat(
      progresses.map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
    );
  }
  // 결재선이 다 끝나지 않는 단계
  // if (pcScanLedgerApproval.currentStep < pcScanLedgerApproval.approvalStep) {
  // let postApprovalSteps: number[] = [];
  // for (let i = 1; i <= pcScanLedgerApproval.approvalStep; i++) {
  //   postApprovalSteps = postApprovalSteps.concat(i);
  // }
  // console.log(postApprovalSteps);

  approvers = approvers.concat(
    scanLedgerApproval.approvers
      .filter(
        (approver) =>
          // postApprovalSteps.includes(approver.step) &&
          approvers.filter((v) => v.step === approver.step)?.length === 0
      )
      .map((approver) => {
        return {
          step: approver.step,
          approvalStatus: ApprovalStatusType.PROGRESS,
          userId: approver.userId,
          userName: approver.userName,
          positionName: approver.positionName,
          deptName: approver.deptName,
        };
      })
  );

  const handleOpenApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.openPopup();
    } else if (type === 'reject') {
      openRejectPopup.openPopup();
    }
  };

  const handleCloseApprovalPopup = (type: string): void => {
    if (type === 'approval') {
      openApprovalPopup.closePopup();
    } else if (type === 'reject') {
      openRejectPopup.closePopup();
    }
  };

  const handleApprovalReject = (remark: string): void => {
    const popupType = openApprovalPopup.opened !== false ? 'approval' : 'reject';

    if (scanLedgerApproval.progressApprovers.length > 0) {
      const progressApproverInfo = scanLedgerApproval.progressApprovers[0];
      const reqApprovalDto: RequestApprovalDto = {
        userId: progressApproverInfo.userId,
        approvalNo: scanLedgerApproval.approvalNo,
        approvalMethod: scanLedgerApproval.approvalMethod,
        approvalType: scanLedgerApproval.approvalType,
        status: popupType === 'approval' ? 'APPROVAL' : 'REJECT',
        remark,
        proxyNo: selectedApproval?.progressApprovers[0].proxyNo
          ? selectedApproval.progressApprovers[0].proxyNo
          : null,
      };
      approvalDetailApi
        .approvalOrReject(reqApprovalDto)
        .then((resp) => {
          onCloseDetail(false, true);
        })
        .catch((err) => alert(err, 'error', '', undefined, true));
    } else {
      onCloseDetail(false);
    }

    handleCloseApprovalPopup(popupType);
  };

  const getUsageFiles = useCallback(
    async (approvalNo: string, page: number, limit: number): Promise<ScanLedgerUsageFilesDto> => {
      const data = await scanLedgerApprovalApi.usageFiles(approvalNo, page, limit);
      return data;
    },
    []
  );

  const [getUsageFilesReqDto, setGetUsageFilesReqDto] = useState<{ page: number }>({ page: 1 });
  const [loading, setLoading] = useState<boolean>(false);
  const { target } = useIntersectionObserver<HTMLDivElement>({
    onIntersect: ([{ isIntersecting }]) => {
      if (isIntersecting === true && !loading) {
        setLoading(true);
        startProgress();
        setTimeout(async () => {
          try {
            const limit = 50;
            const { count, files } = await getUsageFiles(
              scanLedgerApproval.approvalNo,
              getUsageFilesReqDto.page,
              limit
            );
            setFileCount(count);
            if (files.length > 0) {
              setUsageFiles([...usageFiles, ...files]);
            }

            stopProgress();
            if (files.length >= limit) {
              setLoading(false);
            }
          } catch (error) {
            stopProgress();
            setLoading(true);
          }
        }, 500);

        setGetUsageFilesReqDto({ page: getUsageFilesReqDto.page + 1 });
      }
    },
  });

  return (
    <div className="scan-ledger-detail-root">
      <div
        className={classNames('scan-ledger-main-container', {
          'mg mb-85': isComplete === false,
          // 'mg mb-32': isComplete === true,
        })}
      >
        {/* 신청 유형 아이콘 area */}
        <ApprovalTypeIconArea
          onClose={onCloseDetail}
          type={scanLedgerApproval.approvalType}
          status={scanLedgerApproval.status}
        />

        <TabBox>
          <Tabs
            value={tab}
            onChange={(selected) => {
              setTab(selected);
            }}
          >
            <Tab label={t('userApproval:tab.basicInfo')} index={0} />
            <Tab label={t('userApproval:tab.targetFile')} index={1} />
          </Tabs>
          <TabPanel index={0} value={tab}>
            <div className="detail-body">
              {/* 신청 유형 */}
              <ShowLineContent
                title={t('userApproval:approvalType.$')}
                content={
                  <div className="scan-ledger-type-root">
                    <span>{t(`userApproval:approvalType.${scanLedgerApproval.approvalType}`)}</span>
                  </div>
                }
              />
              {/* 제출 유형 */}
              <ShowLineContent
                title={t('userApproval:scanLedgerReqType.$')}
                content={t(`userApproval:scanLedgerReqType.${scanLedgerApproval.requestType}`)}
              />
              {/* 회사 이름 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl1 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.companyName')}
                  content={scanLedgerApproval.etcInfos.sl1}
                />
              )}
              {/* 파일 보유 목적 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl2 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.fileRetainReason')}
                  content={scanLedgerApproval.etcInfos.sl2}
                />
              )}
              {/* 파일 보유 근거 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl3 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.fileRetainGround')}
                  content={scanLedgerApproval.etcInfos.sl3}
                />
              )}
              {/* 파일 보유 기간 */}
              {/* {scanLedgerApproval.requestType === ScanLedgerUsagesType.EXCEPT && null} */}
              {scanLedgerApproval.requestType === ScanLedgerUsagesType.LEDGER && (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.fileRetainPeriod')}
                  content={`${toLocalDateTimeLangStr(
                    scanLedgerApproval.startDate,
                    getFasooUiComponentLanguage()
                  )} ~ ${toLocalDateTimeLangStr(
                    scanLedgerApproval.endDate,
                    getFasooUiComponentLanguage()
                  )}`}
                />
              )}
              {/* 2022-11-03 - Figma 디자인 요청시 변경 필요 */}
              {scanLedgerApproval.requestType === ScanLedgerUsagesType.EXTEND && (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.fileRetainPeriod')}
                  content={
                    <>
                      {t('userApproval:scanLedgerOption.fileRetainPeriodLimit', {
                        dateStr: toLocalDateTimeLangStr(
                          scanLedgerApproval.endDate,
                          getFasooUiComponentLanguage()
                        ),
                      })}
                    </>
                  }
                />
              )}

              {/* 민감 정보 수집 방법 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl5 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.sensitiveDataCollectMethod')}
                  content={scanLedgerApproval.etcInfos.sl5}
                />
              )}
              {/* 대상 개인 범위 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl6 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.targetPersonScope')}
                  content={scanLedgerApproval.etcInfos.sl6}
                />
              )}
              {/* 대상 인원 수 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl7 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.targetPeopleCount')}
                  content={`${scanLedgerApproval.etcInfos.sl7}`}
                />
              )}
              {/* 기록 항목 수 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl8 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.recordsCount')}
                  content={`${scanLedgerApproval.etcInfos.sl8}`}
                />
              )}
              {/* 사용 부서 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl9 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.usageDept')}
                  content={scanLedgerApproval.etcInfos.sl9}
                />
              )}
              {/* 열람 예정일 */}
              {!scanLedgerApproval.etcInfos ||
                !scanLedgerApproval.etcInfos.sl10_1 ||
                !scanLedgerApproval.etcInfos.sl10_2 || (
                  <ShowLineContent
                    title={t('userApproval:scanLedgerOption.scheduledViewDate')}
                    content={`${toLocalDateTimeLangStr(
                      scanLedgerApproval.etcInfos.sl10_1,
                      getFasooUiComponentLanguage()
                    )} ~ ${toLocalDateTimeLangStr(
                      scanLedgerApproval.etcInfos.sl10_2,
                      getFasooUiComponentLanguage()
                    )}`}
                  />
                )}
              {/* 열람 청구 부서 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl11 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.viewRequestDept')}
                  content={scanLedgerApproval.etcInfos.sl11}
                />
              )}
              {/* 열람 제한 항목 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl12 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.viewRestrictItem')}
                  content={scanLedgerApproval.etcInfos.sl12}
                />
              )}
              {/* 열람 제한 사유 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl4 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.viewRestrictReason')}
                  content={scanLedgerApproval.etcInfos.sl4}
                />
              )}
              {/* 이용 제공 기관 이름 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl13 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.usageProviderName')}
                  content={scanLedgerApproval.etcInfos.sl13}
                />
              )}
              {/* 이용 제공 근거 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl14 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.usageProvideGround')}
                  content={scanLedgerApproval.etcInfos.sl14}
                />
              )}
              {/* 이용 제공 항목 */}
              {!scanLedgerApproval.etcInfos || !scanLedgerApproval.etcInfos.sl15 || (
                <ShowLineContent
                  title={t('userApproval:scanLedgerOption.usageProvideItem')}
                  content={scanLedgerApproval.etcInfos.sl15}
                />
              )}
              {/* 승인 방법  */}
              <ShowLineContent
                classes={classNames('approval-method')}
                title={t('userApproval:approvalMethod.$')}
                content={t(`userApproval:approvalMethod.${scanLedgerApproval.approvalMethod}`)}
              />

              {/* 신청자 */}
              <ShowLineContent
                title={t('userApproval:applicant')}
                content={
                  <UserDetail
                    type="request"
                    dateStr={toLocalDateTimeLangStr(
                      scanLedgerApproval.applyDate,
                      getFasooUiComponentLanguage()
                    )}
                    remark={scanLedgerApproval.remark}
                    userInfo={{
                      userId: scanLedgerApproval.userId,
                      userName: scanLedgerApproval.userName,
                      positionName: scanLedgerApproval.positionName,
                      deptName: scanLedgerApproval.deptName,
                      actorIp: scanLedgerApproval.actorIp,
                    }}
                  />
                }
              />
            </div>
            {/* 결재 상세 정보 */}
            <div className="approval-detail-info-title">
              <div className="title-text">{t('userApproval:approvalDetail.$')}</div>
            </div>
            <div className="detail-body">
              <ShowLineContent
                title=""
                content={
                  <>
                    {approvers.map((approver, idx) => {
                      return (
                        <UserDetail
                          step={approvers.length > 1 ? approver.step : undefined}
                          type="approver"
                          dateStr={
                            approver.approvalDate
                              ? toLocalDateTimeLangStr(
                                  approver.approvalDate,
                                  getFasooUiComponentLanguage()
                                )
                              : null
                          }
                          remark={approver.approvalRemark ? approver.approvalRemark : null}
                          userInfo={approver}
                          key={`approver-${idx}`}
                        />
                      );
                    })}

                    {approvers.length <= 1 || <Divider />}
                  </>
                }
              />

              {cancelApprovalChildren !== undefined && cancelApprovalChildren}
            </div>
          </TabPanel>
          <TabPanel index={1} value={tab}>
            <div className="detail-body">
              <ShowLineContent
                title={
                  <div>
                    <Trans i18nKey="userApproval:total">
                      <span />
                      <span className="usage-files-count">{{ total: fileCount }}</span>
                      <span />
                    </Trans>
                  </div>
                }
                content={
                  <>
                    {usageFiles.map((file, fileIdx) => (
                      <div key={`file-${fileIdx}`} className="pt-10">
                        <Link
                          classes={classNames('file')}
                          onClick={() => {
                            //
                          }}
                          isDownload={!(file.removed || file.revoked)}
                        >
                          {FileIcon(file.name)}
                          {file.name}
                          {/* <ToolTip
                          textAlign="center"
                          horiozionPosition="center"
                          message={
                            file.removed ? (
                              <>
                                <span className={classNames('file-status', 'status-color-FAIL')}>
                                  {t('userApproval:scanLedgerFileInfo.message.REMOVED')}
                                </span>
                                <br />
                                {file.name}
                              </>
                            ) : file.revoked ? (
                              <>
                                <span className={classNames('file-status', 'status-color-FAIL')}>
                                  {t('userApproval:scanLedgerFileInfo.message.REVOKED')}
                                </span>
                                <br />
                                {file.name}
                              </>
                            ) : (
                              ''
                            )
                          }
                        >
                          {file.name}
                        </ToolTip> */}
                        </Link>
                      </div>
                    ))}
                  </>
                }
              />
              <div
                style={{
                  height: '10px',
                  minHeight: `10px`,
                  boxSizing: 'border-box',
                }}
                ref={target}
              />
            </div>
          </TabPanel>
        </TabBox>
      </div>
      {isComplete === false && (
        <ApprovalDetailFooter
          approvalMethod={scanLedgerApproval.approvalMethod}
          handleApprove={() => handleOpenApprovalPopup('approval')}
          handleReject={() => handleOpenApprovalPopup('reject')}
        />
      )}
      {(openApprovalPopup.opened === true || openRejectPopup.opened === true) && (
        <RemarkPopup
          open={
            openApprovalPopup.opened === true ? openApprovalPopup.opened : openRejectPopup.opened
          }
          entity={scanLedgerApproval}
          onSubmit={handleApprovalReject}
          onClose={handleCloseApprovalPopup}
          isReject={openApprovalPopup.opened !== true}
        />
      )}
    </div>
  );
};

export default ScanLedgerDetail;
